/**
 * Client-side translation for PHP-rendered pages, so the JS bundle doesn't
 * have to be rebuilt per language. TranslationsBuilder.php supplies
 * `window.JsTranslations`; Next.js has no such global, so `_js` returns the
 * English input there.
 *
 * Vendored from github.com/iFixit/localize (was @ifixit/localize@1.1.1).
 */

interface LocalizeGlobals {
   JsTranslations?: Record<string, string>;
   App?: { showTranslatedPlaceholder?: boolean };
}

const globals = (): LocalizeGlobals =>
   typeof window === 'undefined' ? {} : (window as unknown as LocalizeGlobals);

let UITranslations: Record<string, string> | undefined = globals().JsTranslations;

/** Set the translation lookup directly instead of loading from window.JsTranslations. */
export function setTranslations(translations: Record<string, string> | undefined): void {
   UITranslations = translations;
}

/**
 * Translates an english string into the user's current language.
 *
 * Example: _js('Done');
 *          _js('Delete my %1', 'guide');
 */
export function _js(origString: string, ...replacements: string[]): string {
   // Callers are largely untyped JS, so coerce rather than trusting the signature.
   const compactedString = String(origString).replace(/\s{2,}/g, ' ');
   const translated = translationTable()[compactedString] || compactedString;

   // Handles replacements like _js("Text %1 text %2", "one", "two"). A single
   // pass with a replacer callback keeps `$` in values literal and stops one
   // replacement from being re-substituted into the next.
   const substituted = translated.replace(
      /%(\d+)/g,
      (match, index: string) => replacements[Number(index) - 1] ?? match
   );

   return globals().App?.showTranslatedPlaceholder
      ? translatedPlaceholder(substituted)
      : substituted;
}

/**
 * Picks the singular or plural string based on `number`, then translates it.
 *
 * Example: ___p(stepCount, '%1 Step', '%1 Steps', String(stepCount));
 */
export function ___p(
   number: number,
   singleString: string,
   pluralString: string,
   ...replacements: string[]
): string {
   return number === 1 ? _js(singleString, ...replacements) : _js(pluralString, ...replacements);
}

/**
 * Replaces a string with filler of the same length, so ?translatePage shows
 * which strings go through _js while layout still reflects the real rendered
 * width.
 *
 * Keep this in sync with LocaleLib::translatedPlaceholder(), which does the
 * same for ___().
 */
function translatedPlaceholder(text: string): string {
   const filler = 'Translated ';
   return filler.repeat(Math.ceil(text.length / filler.length)).slice(0, text.length);
}

function translationTable(): Record<string, string> {
   if (!UITranslations) {
      console.error(
         'UI Translations not found! Falling back to returning the input string. Try setTranslations() or window.JsTranslations'
      );
      // Caching the empty table keeps the warning to once per page load.
      UITranslations = {};
   }
   return UITranslations;
}
